import React, { useEffect, useState, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "hooks/useFirebase";
import { request } from "hooks/useOkoApi/useOkoApi";
import { GOOGLE_OAUTH_STATE_KEY, GOOGLE_OAUTH_ORIGINAL_PATH } from "utils/googleAuth";
import { CLEVER_OAUTH_STATE_KEY, CLEVER_OAUTH_ORIGINAL_PATH } from "utils/cleverAuth";
import { CLASSLINK_OAUTH_STATE_KEY, CLASSLINK_OAUTH_ORIGINAL_PATH } from "utils/classlinkAuth";
import { constants, logAuth, logError, LogCategory, LogLevel } from "utils";
import { PENDING_LOGIN_METHOD_KEY } from "utils/analytics";
import { LoginCard } from "components/pages/Home/LoginCard";
import { ButtonContent, Container, Divider, StudentButton, Wrapper } from "components/pages/Home/Login";
import styled from "styled-components";

const normalizePath = (path) => {
  if (!path) return "/";

  let normalizedPath = path;

  // If it's a full URL, extract just the path portion
  if (normalizedPath.includes("://")) {
    try {
      const urlObj = new URL(normalizedPath);
      normalizedPath = urlObj.pathname + urlObj.search;
    } catch (e) {
      console.error("Error parsing URL:", e);
      logError("OAuth URL parsing failed", e, {
        category: LogCategory.AUTH,
        urlAttempted: normalizedPath?.substring(0, 100),
      });
      return "/";
    }
  }

  // Remove any leading '/auth' if it's incorrectly prepended
  if (normalizedPath.startsWith("/auth/") && !normalizedPath.startsWith("/auth/callback")) {
    normalizedPath = normalizedPath.substring(5);
  }

  // Ensure the path starts with a slash
  if (!normalizedPath.startsWith("/")) {
    normalizedPath = "/" + normalizedPath;
  }

  return normalizedPath;
};

export const OAuthHandler = () => {
  const location = useLocation();
  const history = useHistory();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHandled, setIsHandled] = useState(false);
  const [noAccountError, setNoAccountError] = useState(false);
  const [noAccountContext, setNoAccountContext] = useState(null);
  const hasLoggedNoAccountPage = useRef(false);
  // Synchronous guard against the same OAuth code being POSTed twice. The
  // useState-based isProcessing/isHandled guards aren't reliable because
  // setState is batched — a parallel effect fire can read the stale "false"
  // value before React commits the update, race the first fire to the
  // network, and trip a 400 on the second POST since the code is single-use.
  // useRef updates synchronously and survives re-renders without triggering
  // them.
  const inFlightOauthCode = useRef(null);

  useEffect(() => {
    if (isHandled || isProcessing || noAccountError) return;

    // Only process OAuth callbacks on the /auth/callback path
    // This prevents processing group codes on the home page (/)
    if (location.pathname !== "/auth/callback") {
      console.log("OAuthHandler: Not on OAuth callback path, skipping processing");
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const authCode = urlParams.get("code");
    const authState = urlParams.get("state");

    // Synchronous re-entry guard. If we've already started processing this
    // exact OAuth code in a previous effect-fire (state setters hadn't
    // committed yet), bail before kicking off a duplicate handleOAuthRedirect.
    if (authCode && inFlightOauthCode.current === authCode) {
      return;
    }
    if (authCode) {
      inFlightOauthCode.current = authCode;
    }

    // Check Google, Clever, and ClassLink state keys
    const savedGoogleState = localStorage.getItem(GOOGLE_OAUTH_STATE_KEY);
    const savedCleverState = localStorage.getItem(CLEVER_OAUTH_STATE_KEY);
    const savedClassLinkState = localStorage.getItem(CLASSLINK_OAUTH_STATE_KEY);
    const googleOriginalPath = localStorage.getItem(GOOGLE_OAUTH_ORIGINAL_PATH);
    const cleverOriginalPath = localStorage.getItem(CLEVER_OAUTH_ORIGINAL_PATH);
    const classlinkOriginalPath = localStorage.getItem(CLASSLINK_OAUTH_ORIGINAL_PATH);

    if (authCode && authState) {
      if (savedGoogleState === authState) {
        logAuth("OAuth callback processing started", {
          authMethod: constants.GOOGLE,
          hasStateParam: true,
          originPath: googleOriginalPath,
        });
        handleOAuthRedirect(authCode, authState, constants.GOOGLE);
      } else if (savedCleverState === authState) {
        logAuth("OAuth callback processing started", {
          authMethod: constants.CLEVER,
          hasStateParam: true,
          originPath: cleverOriginalPath,
        });
        handleOAuthRedirect(authCode, authState, constants.CLEVER);
      } else if (savedClassLinkState === authState) {
        logAuth("OAuth callback processing started", {
          authMethod: constants.CLASSLINK,
          hasStateParam: true,
          originPath: classlinkOriginalPath,
        });
        handleOAuthRedirect(authCode, authState, constants.CLASSLINK);
      } else {
        logAuth("OAuth callback failed", {
          level: LogLevel.WARNING,
          errorType: "state_mismatch",
          errorMessage: "Invalid authentication state. This could be due to an expired session or security issue.",
          originPath: googleOriginalPath || cleverOriginalPath || classlinkOriginalPath,
        });
        redirectWithError("Invalid authentication state. This could be due to an expired session or security issue.");
      }
    } else if (authCode && !authState) {
      // Clever-initiated login (from Clever Portal) - no state parameter sent by Clever
      // According to Clever docs: "When Clever initiates login, a state parameter is not relevant nor sent"
      // ClassLink LaunchPad-initiated flows route through /auth/classlink/initiate
      // which seeds state before redirecting to the IdP, so callbacks always
      // arrive here with both code + state and are handled in the branch above.
      console.log("Detected Clever-initiated login (no state parameter)");
      logAuth("OAuth callback processing started", {
        authMethod: constants.CLEVER,
        hasStateParam: false,
        isPortalInitiated: true,
        originPath: cleverOriginalPath,
      });
      handleOAuthRedirect(authCode, null, constants.CLEVER);
    } else if (!isHandled) {
      logAuth("OAuth callback failed", {
        level: LogLevel.WARNING,
        errorType: "missing_parameters",
        errorMessage: "Missing authentication parameters. Please try signing in again.",
        originPath: googleOriginalPath || cleverOriginalPath || classlinkOriginalPath,
      });
      redirectWithError("Missing authentication parameters. Please try signing in again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, history, isProcessing, isHandled, noAccountError]);

  const redirectWithError = (errorMessage) => {
    if (noAccountError) {
      return
    }
    // Check Google, Clever, and ClassLink original paths
    const googleOriginalPath = localStorage.getItem(GOOGLE_OAUTH_ORIGINAL_PATH);
    const cleverOriginalPath = localStorage.getItem(CLEVER_OAUTH_ORIGINAL_PATH);
    const classlinkOriginalPath = localStorage.getItem(CLASSLINK_OAUTH_ORIGINAL_PATH);
    const originalPath = googleOriginalPath || cleverOriginalPath || classlinkOriginalPath;

    // Clean up all provider states
    localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
    localStorage.removeItem(GOOGLE_OAUTH_ORIGINAL_PATH);
    localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
    localStorage.removeItem(CLEVER_OAUTH_ORIGINAL_PATH);
    localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
    localStorage.removeItem(CLASSLINK_OAUTH_ORIGINAL_PATH);
    setIsHandled(true);

    // Normalize and use the original path if available, otherwise default to home
    let redirectPath = normalizePath(originalPath) || "/";

    const searchParams = new URLSearchParams(
      redirectPath.includes("?") ?
        redirectPath.substring(redirectPath.indexOf("?")) : ""
    );
    searchParams.set("auth_error", errorMessage);
    const basePath = redirectPath.split("?")[0];
    const finalPath = basePath + "?" + searchParams.toString();
    history.replace(finalPath);
  };

  const handleOAuthRedirect = async (code, stateStr, provider = constants.GOOGLE) => {
    setIsProcessing(true);
    const handlerStartTime = performance.now();
    // Get the origin path from localStorage for logging
    let originPath;
    if (provider === constants.CLEVER) {
      originPath = localStorage.getItem(CLEVER_OAUTH_ORIGINAL_PATH);
    } else if (provider === constants.CLASSLINK) {
      originPath = localStorage.getItem(CLASSLINK_OAUTH_ORIGINAL_PATH);
    } else {
      originPath = localStorage.getItem(GOOGLE_OAUTH_ORIGINAL_PATH);
    }
    const isCustomOidcProvider =
      provider === constants.CLEVER || provider === constants.CLASSLINK;
    // Parse the state to get the destination - declare outside try for catch access
    let targetDestination = "/";
    let isEducatorLogin = false;
    let isEducatorSignUp = false;

    try {
      if (stateStr) {
        // Login with state parameter
        try {
          const stateObj = JSON.parse(stateStr);
          if (stateObj && stateObj.destination) {
            targetDestination = normalizePath(stateObj.destination);
          }
          if (stateObj && stateObj.isEducator) {
            isEducatorLogin = true;
          }
          if (stateObj && stateObj.isSignUp) {
            isEducatorSignUp = true;
          }
        } catch (e) {
          console.error("Error parsing state parameter:", e);
          logError("OAuth state parameter parsing failed", e, {
            category: LogCategory.AUTH,
          });
          // Fallback to stored path if state parsing fails
          const googlePath = localStorage.getItem(GOOGLE_OAUTH_ORIGINAL_PATH);
          const cleverPath = localStorage.getItem(CLEVER_OAUTH_ORIGINAL_PATH);
          const storedPath = googlePath || cleverPath;
          if (storedPath) {
            targetDestination = normalizePath(storedPath);
          }
        }
      } else if (provider === constants.CLEVER) {
        // Clever-initiated login (from Clever Portal) - no state parameter
        // Use a default destination for Clever Portal logins
        const cleverPath = localStorage.getItem(CLEVER_OAUTH_ORIGINAL_PATH);
        if (cleverPath) {
          targetDestination = normalizePath(cleverPath);
        }
        // If no stored path, default to home page
        console.log("Handling Clever-initiated login without state parameter, redirecting to:", targetDestination);
      }

      let authEndpoint;
      if (provider === constants.CLEVER) {
        authEndpoint = "auth/clever";
      } else if (provider === constants.CLASSLINK) {
        authEndpoint = "auth/classlink";
      } else if (isEducatorSignUp) {
        authEndpoint = "auth/google/educator/signup/minimal";
      } else if (isEducatorLogin) {
        authEndpoint = "auth/google/educator";
      } else {
        authEndpoint = "auth/google";
      }
      const response = await request(authEndpoint, {
        method: "POST",
        payload: {
          code: code,
          redirect_uri: `${window.location.origin}/auth/callback`
        },
        skipAuth: true // Do not require JWT token for the auth request itself
      });

      if (response && response.firebase_token) {
        try {
          sessionStorage.setItem(PENDING_LOGIN_METHOD_KEY, provider);
        } catch (_) {
          // sessionStorage unavailable (private mode, etc.) — non-fatal.
        }
        await signInWithCustomToken(auth, response.firebase_token);

        // For Clever / ClassLink OAuth, check if user is a teacher and redirect to educator page
        if (isCustomOidcProvider && response.user_role === "teacher") {
          try {
            // GET /account/educator returns 200 for BOTH existing educators AND
            // first-time signups (it creates a basal OkoUser via
            // _get_or_create_oko_user_from_firebase_uid and returns a pre-filled
            // signup config). HTTP status alone can't distinguish the two — use
            // response.codes: existing educators have at least one group code,
            // first-time signups have an empty array.
            const educatorResponse = await request("account/educator", {
              method: "GET",
            });
            const isExistingEducator = (educatorResponse?.codes?.length ?? 0) > 0;
            const destination = isExistingEducator ? "/educator" : "/educator/settings";
            logAuth("OAuth callback success", {
              authMethod: provider,
              userRole: response.user_role,
              originPath,
              redirectDestination: destination,
              isNewEducator: !isExistingEducator,
            });
            setIsHandled(true);
            history.replace(destination);
            return;
          } catch (e) {
            // Endpoint genuinely errored (no matching school for email domain,
            // network failure, etc). Send to the signup form so the user can
            // self-correct.
            logAuth("OAuth callback success", {
              authMethod: provider,
              userRole: response.user_role,
              originPath,
              redirectDestination: "/educator/settings",
              isNewEducator: true,
            });
            setIsHandled(true);
            history.replace("/educator/settings");
            return;
          }
        }

        // Clean up URL parameters to prevent loops
        let cleanDestination = targetDestination;

        // Only apply special handling for Clever / ClassLink OAuth
        if (isCustomOidcProvider) {
          try {
            // For OIDC OAuth with group code in state, redirect to GroupCode with the group code
            if (stateStr) {
              try {
                const stateObj = JSON.parse(stateStr);
                if (stateObj.groupCode) {
                  // We have the group code in state, so redirect to GroupCode with the group code
                  // This is cleaner than relying on OAuth state processing
                  cleanDestination = `/?code=${stateObj.groupCode}`;
                }
              } catch (e) {
                console.error("Error parsing state for group code cleanup:", e);
              }
            }

            // Without a group code redirect, clean up OAuth-related parameters
            if (!cleanDestination.includes("/?code=")) {
              const destUrl = new URL(cleanDestination, window.location.origin);
              const originalHref = destUrl.href;

              // Remove OAuth-related parameters that could cause loops
              destUrl.searchParams.delete('code');
              destUrl.searchParams.delete('state');
              destUrl.searchParams.delete('auth_error');

              // Use cleaned URL if parameters were removed
              if (destUrl.href !== originalHref) {
                cleanDestination = destUrl.pathname + destUrl.search;
              }
            }
          } catch (e) {
            console.error("Error cleaning destination URL:", e);
            // Fallback to original destination if URL parsing fails
          }
        }
        // For Google OAuth, keep cleanDestination = targetDestination (no changes)

        // Clean up localStorage items based on provider and login type
        if (provider === constants.CLEVER) {
          // Always clean up Clever-specific path
          localStorage.removeItem(CLEVER_OAUTH_ORIGINAL_PATH);

          // Only clean up Clever OAuth state if we're NOT redirecting to a group code URL
          // The GroupCode component needs the state to know the user just completed OAuth
          if (!cleanDestination.includes("/?code=")) {
            localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
          }

          // For Clever-initiated logins (no state), also clean up any Google items
          // in case user had a previous Google session
          if (!stateStr) {
            localStorage.removeItem(GOOGLE_OAUTH_ORIGINAL_PATH);
            localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
            // Also clean up legacy Google OAuth key for backward compatibility
            localStorage.removeItem("oauth_original_path");
          }
        } else if (provider === constants.CLASSLINK) {
          localStorage.removeItem(CLASSLINK_OAUTH_ORIGINAL_PATH);

          // Mirror Clever: keep state if redirecting to a group-code URL so GroupCode
          // sees that an OAuth flow just completed.
          if (!cleanDestination.includes("/?code=")) {
            localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
          }
        } else {
          // Google OAuth cleanup
          localStorage.removeItem(GOOGLE_OAUTH_ORIGINAL_PATH);

          // For Google OAuth, check if we have group code in state to determine if we should preserve state
          let shouldPreserveGoogleState = false;
          if (stateStr) {
            try {
              const stateObj = JSON.parse(stateStr);
              // Preserve state if we have group code info that GroupCode component will need
              shouldPreserveGoogleState = stateObj.groupCode || stateObj.authMethods;
            } catch (e) {
              console.error("Error parsing Google state for cleanup decision:", e);
            }
          }

          if (!shouldPreserveGoogleState) {
            localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
          }

          // Also clean up any leftover Clever / ClassLink OAuth state to prevent interference
          localStorage.removeItem(CLEVER_OAUTH_ORIGINAL_PATH);
          localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
          localStorage.removeItem(CLASSLINK_OAUTH_ORIGINAL_PATH);
          localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
        }

        // Log success for non-teacher flows
        logAuth("OAuth callback success", {
          authMethod: provider,
          userRole: response.user_role,
          originPath,
          redirectDestination: cleanDestination,
        });

        setIsHandled(true);
        console.log("Redirecting to cleaned destination:", cleanDestination);
        history.replace(cleanDestination);
      } else {
        throw new Error("Invalid response from the authentication endpoint");
      }
    } catch (e) {
      // A failed sign-in must not leave its marker behind, or the next reload
      // in this tab would be counted as a Login.
      try {
        sessionStorage.removeItem(PENDING_LOGIN_METHOD_KEY);
      } catch (_) {
        // sessionStorage unavailable — nothing to clear.
      }
      console.error("Error during OAuth callback:", e);
      console.log("Error status:", e?.status, "Error statusCode:", e?.statusCode);

      const errorStatus = Number(e?.status) || Number(e?.statusCode);
      const is403Error = errorStatus === 403;
      console.log("Is 403 error:", is403Error, "errorStatus:", errorStatus);

      if (is403Error) {
        // Log 403 "no account" error as distinct event for tracking conversion opportunities
        logAuth("OAuth no account error", {
          level: LogLevel.WARNING,
          authMethod: provider,
          originPath,
          errorStatus: 403,
          isEducatorAttempt: isEducatorLogin || isEducatorSignUp,
          hasStateParam: !!stateStr,
          durationMs: Math.round(performance.now() - handlerStartTime),
        });

        // Store context for subsequent logs before clearing localStorage
        setNoAccountContext({ originPath, authMethod: provider });

        localStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
        localStorage.removeItem(GOOGLE_OAUTH_ORIGINAL_PATH);
        localStorage.removeItem(CLEVER_OAUTH_STATE_KEY);
        localStorage.removeItem(CLEVER_OAUTH_ORIGINAL_PATH);
        localStorage.removeItem(CLASSLINK_OAUTH_STATE_KEY);
        localStorage.removeItem(CLASSLINK_OAUTH_ORIGINAL_PATH);
        setNoAccountError(true);
        setIsHandled(true);

        return;
      }

      // Log general OAuth callback failure
      const is4xx = errorStatus >= 400 && errorStatus < 500;
      logAuth("OAuth callback failed", {
        level: is4xx ? LogLevel.WARNING : LogLevel.ERROR,
        authMethod: provider,
        originPath,
        errorStatus: errorStatus || undefined,
        errorDetail: e?.detail,
        errorMessage: e?.message,
        errorStack: e?.stack,
        hasStateParam: !!stateStr,
        isEducatorAttempt: isEducatorLogin || isEducatorSignUp,
        durationMs: Math.round(performance.now() - handlerStartTime),
        codeLength: code?.length,
        retryCount: e?.retryCount,
      });

      setIsProcessing(false);

      redirectWithError(e?.detail || e?.message || "An unknown error occurred during authentication");
    }
  };

  // Log when no account page is displayed (only once)
  useEffect(() => {
    if (noAccountError && noAccountContext && !hasLoggedNoAccountPage.current) {
      logAuth("No account page displayed", {
        authMethod: noAccountContext.authMethod,
        originPath: noAccountContext.originPath,
        pageType: "request_demo",
      });
      hasLoggedNoAccountPage.current = true;
    }
  }, [noAccountError, noAccountContext]);

  const handleRequestDemoClick = () => {
    logAuth("Request demo clicked", {
      authMethod: noAccountContext?.authMethod || "oauth",
      originPath: noAccountContext?.originPath,
      action: "request_demo",
    });
  };

  const handleBackToLoginClick = () => {
    logAuth("Back to teacher login clicked", {
      authMethod: noAccountContext?.authMethod || "oauth",
      originPath: noAccountContext?.originPath,
      action: "back_to_login",
    });
  };

  if (noAccountError) {
    return (
      <Container isEducatorLogin>
        <LoginCard borderRadius={1} maxWidth={394}>
          <Wrapper gap={'1.5rem'}>
            <svg width="243" height="100" viewBox="0 0 243 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M185.575 42.7517C177.311 42.7517 170.606 36.9423 170.606 29.839C170.606 22.7358 177.311 16.9264 185.575 16.9264C193.838 16.9264 200.544 22.7358 200.544 29.839C200.544 36.9423 193.812 42.7517 185.575 42.7517ZM185.575 0C166.83 0 151.65 13.3615 151.65 29.839C151.65 46.3165 166.857 59.6781 185.575 59.6781C204.32 59.6781 219.5 46.3165 219.5 29.839C219.5 13.3615 204.32 0 185.575 0ZM57.4515 42.7517C49.188 42.7517 42.4822 36.9423 42.4822 29.839C42.4822 22.7358 49.188 16.9264 57.4515 16.9264C65.715 16.9264 72.4208 22.7358 72.4208 29.839C72.4208 36.9423 65.6886 42.7517 57.4515 42.7517ZM57.4251 0C38.7069 0 23.5 13.3615 23.5 29.839C23.5 46.3165 38.7069 59.6781 57.4251 59.6781C76.1433 59.6781 91.3502 46.3165 91.3502 29.839C91.3766 13.3615 76.1697 0 57.4251 0ZM134.146 29.839L160.811 0.818593H137.103L114.583 25.2179V0.844999H95.1783V58.8595H114.583V34.5129L136.997 58.8859H160.811L134.014 29.839H134.146Z" fill="#FE1E7E" />
              <path d="M2.0593 94.678V80.678H4.7193V92.318H11.9993V94.678H2.0593ZM14.1687 94.678V80.678H25.1087V82.978H16.8287V86.118H22.5087V88.458H16.8287V92.338H25.4887V94.678H14.1687ZM31.9545 94.678V83.038H26.6745V80.678H39.8945V83.038H34.6145V94.678H31.9545ZM42.0598 86.758L41.3798 83.078V80.678H43.7998V83.078L43.1198 86.758H42.0598ZM52.6277 94.958C49.1677 94.958 46.7077 93.358 45.4677 92.138L46.8877 90.198C48.3277 91.518 50.3077 92.578 52.6677 92.578C54.4477 92.578 55.8677 92.038 55.8677 90.658C55.8677 89.758 55.2277 89.338 53.7877 89.058L50.9877 88.538C48.0477 87.978 46.3477 86.858 46.3477 84.618C46.3477 82.058 48.4877 80.398 51.9477 80.398C54.5477 80.398 56.4877 81.238 58.3077 82.298L57.2877 84.418C55.6277 83.438 53.8677 82.758 51.8477 82.758C49.9677 82.758 49.0677 83.398 49.0877 84.398C49.0877 85.278 49.9877 85.738 51.6277 86.018L54.2877 86.498C57.0677 86.978 58.6677 88.058 58.6677 90.478C58.6677 93.318 56.2677 94.958 52.6277 94.958ZM72.078 94.958C68.218 94.958 65.458 92.018 65.458 87.878C65.458 83.478 68.338 80.398 72.818 80.398C75.458 80.398 77.658 81.598 78.958 83.778L76.718 85.158C75.878 83.838 74.818 82.778 72.738 82.778C69.898 82.778 68.218 84.718 68.218 87.758C68.218 90.558 69.818 92.618 72.618 92.618C74.578 92.618 76.158 91.578 76.618 89.458V89.438H73.358V87.098H79.018V94.678H76.938L76.838 92.278C75.838 94.078 74.158 94.958 72.078 94.958ZM82.0593 94.678V80.678H92.9993V82.978H84.7193V86.118H90.3993V88.458H84.7193V92.338H93.3793V94.678H82.0593ZM99.8451 94.678V83.038H94.5651V80.678H107.785V83.038H102.505V94.678H99.8451ZM117.422 94.678V89.498L112.102 80.678H115.102L118.802 86.958L122.502 80.678H125.442L120.082 89.498V94.678H117.422ZM132.831 94.958C128.691 94.958 125.751 91.918 125.751 87.678C125.751 83.438 128.691 80.398 132.831 80.398C136.991 80.398 139.951 83.438 139.951 87.678C139.951 91.918 136.991 94.958 132.831 94.958ZM132.831 92.598C135.411 92.598 137.171 90.618 137.171 87.678C137.171 84.738 135.411 82.758 132.831 82.758C130.271 82.758 128.511 84.738 128.511 87.678C128.511 90.618 130.271 92.598 132.831 92.598ZM148.445 94.958C144.705 94.958 142.525 92.978 142.525 89.518V80.678H145.185V89.278C145.185 91.478 146.385 92.538 148.445 92.538C150.565 92.538 151.765 91.478 151.765 89.278V80.678H154.425V89.518C154.425 92.978 152.205 94.958 148.445 94.958ZM168.702 94.958C165.242 94.958 162.782 93.358 161.542 92.138L162.962 90.198C164.402 91.518 166.382 92.578 168.742 92.578C170.522 92.578 171.942 92.038 171.942 90.658C171.942 89.758 171.302 89.338 169.862 89.058L167.062 88.538C164.122 87.978 162.422 86.858 162.422 84.618C162.422 82.058 164.562 80.398 168.022 80.398C170.622 80.398 172.562 81.238 174.382 82.298L173.362 84.418C171.702 83.438 169.942 82.758 167.922 82.758C166.042 82.758 165.142 83.398 165.162 84.398C165.162 85.278 166.062 85.738 167.702 86.018L170.362 86.498C173.142 86.978 174.742 88.058 174.742 90.478C174.742 93.318 172.342 94.958 168.702 94.958ZM177.411 94.678V80.678H188.351V82.978H180.071V86.118H185.751V88.458H180.071V92.338H188.731V94.678H177.411ZM195.197 94.678V83.038H189.917V80.678H203.137V83.038H197.857V94.678H195.197ZM215.378 94.958C211.638 94.958 209.458 92.978 209.458 89.518V80.678H212.118V89.278C212.118 91.478 213.318 92.538 215.378 92.538C217.498 92.538 218.698 91.478 218.698 89.278V80.678H221.358V89.518C221.358 92.978 219.138 94.958 215.378 94.958ZM224.774 94.678V80.678H231.674C234.634 80.678 236.414 82.418 236.414 84.978C236.414 87.638 234.474 89.458 231.474 89.458H227.434V94.678H224.774ZM230.914 87.098C232.654 87.098 233.694 86.398 233.694 84.978C233.694 83.638 232.754 82.958 231.054 82.958H227.434V87.098H230.914ZM239.258 90.618L238.678 84.078V80.678H241.138V84.078L240.538 90.618H239.258ZM238.458 94.678V91.938H241.338V94.678H238.458Z" fill="#FE1E7E" />
            </svg>
            <Divider />
            <Text>It seems you're ready to dive in, but you don't have an OKO account yet. No problem! To see OKO in action and learn how to get started, the next step is to schedule a quick demo with our team.</Text>
            <ButtonContent>
              <StudentButton href="https://www.okolabs.ai/form" target="_blank" onClick={handleRequestDemoClick}>
                <Text>Request a Demo</Text>
              </StudentButton>
            </ButtonContent>

            <Link href="/educator/login" onClick={handleBackToLoginClick}>Back to Teacher Login</Link>
          </Wrapper>
        </LoginCard>
      </Container>
    );
  }

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
      <div className="text-center">
        <FontAwesomeIcon icon={faSpinner} spin size="5x" className="mb-3" />
        <h3>Signing in...</h3>
      </div>
    </div>
  );
};

const Text = styled.p`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.25rem;
  text-align: center;
  max-wdith: 314px;
  width: 100%;
`

const Link = styled.a`
  font-family: Roboto;
  font-weight: 700;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  text-decoration: none;
  color: #605DEC;
`
